import React, { useState, useEffect } from 'react';
import { Settings, Save, AlertCircle } from 'lucide-react';
import barrelReplenishmentService from '../../../services/barrelReplenishmentService';

const ReplenishmentRulesPanel = ({ onRefresh }) => {
  const [rules, setRules] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rulesData, suppliersData] = await Promise.all([
        barrelReplenishmentService?.getReplenishmentRules(),
        barrelReplenishmentService?.getSuppliers({ isActive: true })
      ]);
      setRules(rulesData);
      setSuppliers(suppliersData);
    } catch (error) {
      console.error('Error loading rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRule = async (ruleId) => {
    try {
      setSaving(true);
      await barrelReplenishmentService?.updateReplenishmentRule(ruleId, editingRule);
      await loadData();
      setEditingRule(null);
      onRefresh?.();
    } catch (error) {
      console.error('Error saving rule:', error);
      alert('Erreur lors de la sauvegarde de la règle');
    } finally {
      setSaving(false);
    }
  };

  const getBarrelTypeLabel = (type) => {
    return type === 'noir_270l' ? 'Noir 270L' : 'Bleu 220L';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Settings className="w-5 h-5 text-blue-600" />
          Règles de Réapprovisionnement
        </h2>
      </div>
      <div className="space-y-4">
        {rules?.map((rule) => (
          <div key={rule?.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">
                  {getBarrelTypeLabel(rule?.barrel_type)}
                </span>
                {rule?.is_active ? (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                    Actif
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                    Inactif
                  </span>
                )}
              </div>
              <button
                onClick={() => setEditingRule(editingRule?.id === rule?.id ? null : rule)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                {editingRule?.id === rule?.id ? 'Annuler' : 'Modifier'}
              </button>
            </div>

            {editingRule?.id === rule?.id ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Seuil Minimum
                    </label>
                    <input
                      type="number"
                      value={editingRule?.minimum_stock_threshold || ''}
                      onChange={(e) =>
                        setEditingRule({
                          ...editingRule,
                          minimum_stock_threshold: parseInt(e?.target?.value)
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantité de Commande
                    </label>
                    <input
                      type="number"
                      value={editingRule?.reorder_quantity || ''}
                      onChange={(e) =>
                        setEditingRule({
                          ...editingRule,
                          reorder_quantity: parseInt(e?.target?.value)
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fournisseur Préféré
                  </label>
                  <select
                    value={editingRule?.preferred_supplier_id || ''}
                    onChange={(e) =>
                      setEditingRule({
                        ...editingRule,
                        preferred_supplier_id: e?.target?.value || null
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner un fournisseur</option>
                    {suppliers?.map((supplier) => (
                      <option key={supplier?.id} value={supplier?.id}>
                        {supplier?.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingRule?.auto_order_enabled || false}
                      onChange={(e) =>
                        setEditingRule({
                          ...editingRule,
                          auto_order_enabled: e?.target?.checked
                        })
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Commande automatique</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingRule?.approval_required || false}
                      onChange={(e) =>
                        setEditingRule({
                          ...editingRule,
                          approval_required: e?.target?.checked
                        })
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Approbation requise</span>
                  </label>
                </div>

                <button
                  onClick={() => handleSaveRule(rule?.id)}
                  disabled={saving}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-600">Seuil minimum:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {rule?.minimum_stock_threshold} unités
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Quantité commande:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {rule?.reorder_quantity} unités
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">Fournisseur préféré:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {rule?.barrel_suppliers?.name || 'Non défini'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Commande auto:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {rule?.auto_order_enabled ? 'Oui' : 'Non'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Approbation:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {rule?.approval_required ? 'Requise' : 'Non requise'}
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {rules?.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <AlertCircle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>Aucune règle de réapprovisionnement configurée</p>
        </div>
      )}
    </div>
  );
};

export default ReplenishmentRulesPanel;